import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ArticleDetail from '../components/ArticleDetail';
import sanityClient from '../lib/sanityClient';
import SEO from '../components/SEO';

function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError('No article ID provided');
      setIsLoading(false);
      return;
    }

    sanityClient
      .fetch(
        `*[_type == "news" && slug.current == $id][0]{
          title,
          "date": publishedAt,
          author,
          "content": content,
          "heroImage": mainImage.asset->url,
          "relatedArticles": *[_type == "news" && slug.current != $id][0...3]{
            _id,
            title,
            "image": mainImage.asset->url,
            "date": publishedAt,
            slug
          }
        }`,
        { id }
      )
      .then((data) => {
        if (!data) {
          setError('Article not found');
        } else {
          // Format the date
          const formattedArticle = {
            ...data,
            date: data.date
              ? new Date(data.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'No date',
            // HTML content from the rich text editor
            content: data.content ? processRichText(data.content) : '<p>No content available.</p>',
            // Format dates for related articles
            relatedArticles: data.relatedArticles
              ? data.relatedArticles.map((related) => ({
                  ...related,
                  date: related.date
                    ? new Date(related.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'No date',
                }))
              : [],
          };

          setArticle(formattedArticle);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching article:', err);
        setError('Failed to load article. Please try again later.');
        setIsLoading(false);
      });
  }, [id]);

  // Process rich text content from Sanity
  function processRichText(content) {
    // This is a simple placeholder - in a real app, use @portabletext/react or similar
    return `<p>This article content would be properly rendered with Sanity's Portable Text to HTML converter in a production app.</p>
    <p>For now, we're displaying this placeholder text to represent the article content.</p>
    <p>The article has ${content.length} blocks of rich text content.</p>`;
  }

  if (isLoading) {
    return (
      <>
        <SEO
          title={`${article.title} - Bombardiers Hockey News`}
          description={article.excerpt || 'Latest news from the Bombardiers Hockey team.'}
          image={article.heroImage || '/bomb-puck-logo.png'}
          type="article"
        />
        <div className="bg-black min-h-screen flex justify-center items-center">
          <div className="text-white text-xl">Loading article...</div>
        </div>
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <SEO
          title={`${article.title} - Bombardiers Hockey News`}
          description={article.excerpt || 'Latest news from the Bombardiers Hockey team.'}
          image={article.heroImage || '/bomb-puck-logo.png'}
          type="article"
        />
        <div className="bg-black min-h-screen flex justify-center items-center">
          <div className="bg-gray-900 p-6 rounded-lg text-white text-xl">
            {error || 'Article not found'}
          </div>
        </div>
      </>
    );
  }

  return <ArticleDetail article={article} />;
}

export default ArticlePage;
